import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'

import { zIndex } from 'constants/ui'
import useClickOutside from 'hooks/useClickOutside'

import { StyledCaretDownIcon } from './Select'

type ToggleSize = {
	height: string
	width: string
	fontSize: string
	caret: number
	caretGap: string
}

const toggleSizeMap: Record<'small' | 'medium', ToggleSize> = {
	small: {
		height: '17px',
		width: '42px',
		fontSize: '9px',
		caret: 9,
		caretGap: '3px',
	},
	medium: {
		height: '25px',
		width: '50px',
		fontSize: '12px',
		caret: 12,
		caretGap: '5px',
	},
}

type ToggleProps = {
	values: Record<number, string>
	selectedValue: number
	defaultLabel?: string
	isModal?: boolean
	openZIndex?: number
	onChange: (value: number) => void
	size?: keyof typeof toggleSizeMap
}

const getLabelByValue = (value: number, values: Record<number, string>, defaultValue: string) =>
	values[value] ?? defaultValue

const RoundedSelect: FC<ToggleProps> = ({
	values,
	selectedValue,
	onChange,
	openZIndex,
	defaultLabel = 'Default',
	isModal = false,
	size = 'medium',
}) => {
	const [open, setOpen] = useState(false)
	const [position, setPosition] = useState({ top: 0, left: 0, zIndex: 0 })
	const { ref: rowsContainerRef, excludeRef: containerRef } = useClickOutside(() => setOpen(false))

	const toggleSize = useMemo(() => toggleSizeMap[size], [size])

	const updateValue = useCallback(
		(v: number) => {
			onChange(v)
			setOpen(false)
		},
		[onChange]
	)

	useEffect(() => {
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect()
			setPosition({
				top: rect.top + 1 + window.scrollY + parseFloat(toggleSize.height),
				left: rect.left + window.scrollX,
				zIndex: openZIndex ?? (isModal ? zIndex.DRAWER : zIndex.BASE),
			})
		}
	}, [isModal, openZIndex, open, toggleSize, containerRef])

	return (
		<ToggleContainer ref={containerRef} open={open}>
			<ToggleTable open={open} size={toggleSize}>
				<ToggleTableHeader open={open} onClick={() => setOpen(!open)} size={toggleSize}>
					{getLabelByValue(selectedValue, values, defaultLabel)}
					<StyledCaretDownIcon width={toggleSize.caret} height={toggleSize.caret} $flip={open} />
				</ToggleTableHeader>
				{open &&
					ReactDOM.createPortal(
						<ToggleTableRows
							ref={rowsContainerRef}
							size={toggleSize}
							style={{
								position: 'absolute',
								top: position.top,
								left: position.left,
								zIndex: position.zIndex,
							}}
						>
							{Object.entries(values).map(([key, value]) => (
								<ToggleTableRow
									key={key}
									onClick={() => updateValue(Number(key))}
									size={toggleSize}
								>
									{value}
								</ToggleTableRow>
							))}
						</ToggleTableRows>,
						document.body
					)}
			</ToggleTable>
		</ToggleContainer>
	)
}

const ToggleTableRow = styled.div<{ size: ToggleSize }>`
	align-items: center;
	display: flex;
	margin: auto;
	padding: 5px 7px;
	height: ${(props) => props.size.height};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.pill['gray'].background};
	border-width: 0px;
	font-size: ${(props) => props.size.fontSize};

	:last-child {
		border-radius: 0px 0px 9px 9px;
	}

	:hover {
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
		background: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.pill['gray'].hover.background};
		:last-child {
			border-radius: 0px 0px 9px 9px;
		}
	}
`

const ToggleTableRows = styled.div<{ size: ToggleSize }>`
	position: absolute;
	top: ${(props) => props.size.height};
	width: ${(props) => props.size.width};
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.pill['gray'].border};
	border-top-width: 0px;
	border-radius: 0px 0px 9px 9px;
	> div:not(:last-child) {
		border-bottom: 1px solid
			${(props) => props.theme.colors.selectedTheme.newTheme.pill['gray'].border};
	}
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
	cursor: pointer;
`

export const ToggleTableHeader = styled.div<{ open: boolean; size: ToggleSize }>`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: ${(props) => props.size.caretGap};
	height: ${(props) => props.size.height};
	border-bottom-style: solid;
	border-bottom-color: ${(props) => props.theme.colors.selectedTheme.newTheme.pill['gray'].border};
	border-bottom-width: ${(props) => (props.open ? '1px' : '0')};
	border-radius: ${(props) => (props.open ? '9px 9px 0px 0px' : '9px')};
`

export const ToggleTable = styled.div<{ open: boolean; size: ToggleSize }>`
	display: flex;
	flex-direction: column;
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.pill['gray'].background};
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	:first-child {
		border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.pill['gray'].border};
	}
	width: ${(props) => props.size.width};
	font-size: ${(props) => props.size.fontSize};
	font-family: ${(props) => props.theme.fonts.bold};
	position: relative;
	border-radius: ${(props) => (props.open ? '9px 9px 0px 0px' : '9px')};
`

const ToggleContainer = styled.div<{ open: boolean }>`
	cursor: pointer;
`

export default RoundedSelect
